import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { FieldLabel } from "@/components/FieldLabel";

interface CustomField {
  id: number;
  fieldKey: string;
  label: string;
  fieldType: "text" | "number" | "date" | "select" | "checkbox";
  isRequired: boolean;
  options?: { values: string[] };
  defaultValue?: string;
  helpText?: string;
  displayOrder: number;
}

interface DynamicCustomFieldsProps {
  fields: CustomField[];
  values: Record<string, any>;
  onChange: (fieldKey: string, value: any) => void;
  showValidation?: boolean; // Only show required indicators when validating
}

export function DynamicCustomFields({ fields, values, onChange, showValidation = false }: DynamicCustomFieldsProps) {
  if (!fields || fields.length === 0) return null;

  const renderField = (field: CustomField) => {
    const value = values[field.fieldKey] ?? field.defaultValue ?? "";

    switch (field.fieldType) {
      case "text":
        return (
          <div key={field.id}>
            <FieldLabel 
              htmlFor={field.fieldKey}
              label={field.label}
              required={field.isRequired && showValidation}
              helpText={field.helpText}
            />
            <Input
              id={field.fieldKey}
              value={value}
              onChange={(e) => onChange(field.fieldKey, e.target.value)}
              className={field.isRequired && showValidation && !value ? "border-destructive" : ""}
            />
          </div>
        );

      case "number":
        return (
          <div key={field.id}>
            <FieldLabel 
              htmlFor={field.fieldKey}
              label={field.label}
              required={field.isRequired && showValidation}
              helpText={field.helpText}
            />
            <Input
              id={field.fieldKey}
              type="number"
              step="any"
              value={value}
              onChange={(e) => onChange(field.fieldKey, e.target.value)}
              className={field.isRequired && showValidation && !value ? "border-destructive" : ""}
            />
          </div>
        );

      case "date":
        return (
          <div key={field.id}>
            <FieldLabel 
              htmlFor={field.fieldKey}
              label={field.label}
              required={field.isRequired && showValidation}
              helpText={field.helpText}
            />
            <Input
              id={field.fieldKey}
              type="date"
              value={value}
              onChange={(e) => onChange(field.fieldKey, e.target.value)}
              className={field.isRequired && showValidation && !value ? "border-destructive" : ""}
            />
          </div>
        );

      case "select":
        return (
          <div key={field.id}>
            <FieldLabel 
              htmlFor={field.fieldKey}
              label={field.label}
              required={field.isRequired && showValidation}
              helpText={field.helpText}
            />
            <Select value={value} onValueChange={(v) => onChange(field.fieldKey, v)}>
              <SelectTrigger id={field.fieldKey}>
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {field.options?.values?.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "checkbox":
        return (
          <div key={field.id} className="flex items-center gap-2">
            <Checkbox
              id={field.fieldKey}
              checked={value === true || value === "true"}
              onCheckedChange={(checked) => onChange(field.fieldKey, checked)}
            />
            <Label htmlFor={field.fieldKey} className="cursor-pointer">
              {field.label}
              {field.isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <div className="border-t pt-4">
        <h3 className="text-sm font-medium mb-3">Additional Fields</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map(renderField)}
        </div>
      </div>
    </div>
  );
}
